import React from "react";
import "./ScreenshotMedia.scss";

type ScreenshotMediaProps = {
  url: string | null;
  name: string;
  mimeType?: string;
  className?: string;
};

const isImageFile = (mimeType?: string, name?: string) =>
  mimeType
    ? mimeType.startsWith("image/")
    : /\.(png|jpe?g)$/i.test(name || "");

const getFileLabel = (name: string) => {
  const extension = name.split(".").pop() || "";
  return extension && extension !== name
    ? extension.slice(0, 4).toUpperCase()
    : "FILE";
};

const FileIcon: React.FC<{ label: string }> = ({ label }) => (
  <svg width="40" height="48" viewBox="0 0 40 48" fill="none" aria-hidden="true">
    <path
      d="M6 1h20l13 13v29a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V5a4 4 0 0 1 4-4Z"
      fill="#fff"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path d="M26 1v9a4 4 0 0 0 4 4h9" stroke="currentColor" strokeWidth="1.5" />
    <rect x="6" y="27" width="28" height="12" rx="2" fill="currentColor" />
    <text
      x="20"
      y="36"
      textAnchor="middle"
      fontFamily="Roboto, sans-serif"
      fontSize="8"
      fontWeight="700"
      fill="#fff"
    >
      {label}
    </text>
  </svg>
);

const ScreenshotMedia: React.FC<ScreenshotMediaProps> = ({
  url,
  name,
  mimeType,
  className,
}) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);

  React.useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
  }, [url]);

  const rootClassName = `screenshot-media${className ? ` ${className}` : ""}`;

  if (!url || hasError || !isImageFile(mimeType, name)) {
    return (
      <div
        className={`${rootClassName} screenshot-media--placeholder`}
        role="img"
        aria-label={name}
        title={name}
      >
        <FileIcon label={getFileLabel(name)} />
        <span className="screenshot-media__name">{name}</span>
      </div>
    );
  }

  return (
    <img
      className={`${rootClassName}${isLoaded ? "" : " screenshot-media--loading"}`}
      src={url}
      alt={name}
      loading="lazy"
      onLoad={() => setIsLoaded(true)}
      onError={() => setHasError(true)}
    />
  );
};

export default ScreenshotMedia;
